import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { TASK_CATEGORIES } from '@/lib/taskCategoryConfig';
import { format, isPast } from 'date-fns';
import { Calendar } from 'lucide-react';
import { AssigneeAvatarGroup } from './AssigneeAvatarGroup';
import { AssignmentOverallStatus } from './AssignmentOverallStatus';
import { getRoleColor } from '@/lib/roleColors';

interface Task {
  id: string;
  task_title: string;
  status: string;
  due_date: string | null;
  assignees: any[];
  checkpoints: any[];
  category_type: string;
  estimated_hours: number | null;
}

interface BoardTaskCardProps {
  task: Task;
  onViewDetails: (task: Task) => void;
}

export function BoardTaskCard({ task, onViewDetails }: BoardTaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Determine effective status based on assignments
  let effectiveStatus = task.status;
  if (task.assignees && task.assignees.length > 0) {
    const allCompleted = task.assignees.every((a: any) => a.assignment_status === 'completed');
    const anyInProgress = task.assignees.some((a: any) => a.assignment_status === 'in_progress');
    const anyBlocked = task.assignees.some((a: any) => a.assignment_status === 'blocked');
    
    if (allCompleted) effectiveStatus = 'completed';
    else if (anyBlocked) effectiveStatus = 'blocked';
    else if (anyInProgress) effectiveStatus = 'in_progress';
  }

  const categoryInfo = TASK_CATEGORIES.find(c => c.id === task.category_type);
  
  // Get primary assignee role color for left border
  const primaryAssignee = task.assignees?.[0];
  const borderColor = primaryAssignee ? getRoleColor(primaryAssignee.member_role).border : 'transparent';

  const getCheckpointProgress = () => {
    if (!task.checkpoints?.length) return 0;
    const completed = task.checkpoints.filter((c: any) => c.completed).length;
    return Math.round((completed / task.checkpoints.length) * 100);
  };

  const getCompletedCheckpoints = () => {
    return task.checkpoints?.filter((c: any) => c.completed).length || 0;
  };

  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && effectiveStatus !== 'completed';

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeftColor: borderColor, borderLeftWidth: '4px' }}
      {...attributes}
      {...listeners}
      className="bg-card rounded-lg p-3 shadow-sm border hover:shadow-md cursor-grab active:cursor-grabbing transition-shadow relative"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
        onViewDetails(task);
      }}
    >
      {/* Assignment Status Badge - Top Right */}
      <div className="absolute top-2 right-2" data-no-drag>
        <AssignmentOverallStatus assignees={task.assignees || []} variant="dot" size="small" />
      </div>
      {/* Category */}
      {categoryInfo && (
        <div className="mb-2">
          <span className="text-xs text-muted-foreground">
            {categoryInfo.name}
          </span>
        </div>
      )}

      {/* Title */}
      <h4 className="font-medium text-sm mb-2 line-clamp-2">
        {task.task_title}
      </h4>

      {/* Due Date */}
      {task.due_date && (
        <div className={`flex items-center gap-1 text-xs mb-2 ${isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
          <Calendar className="h-3 w-3" />
          {format(new Date(task.due_date), 'MMM d')}
          {isOverdue && <span className="ml-1">Overdue</span>}
        </div>
      )}

      {/* Assignees - Compact View */}
      <div className="mb-2" data-no-drag>
        <AssigneeAvatarGroup 
          assignees={task.assignees || []} 
          maxVisible={2}
          size="small"
          showTooltip={true}
        />
      </div>

      {/* Checkpoint Progress */}
      {task.checkpoints && task.checkpoints.length > 0 && (
        <div className="mt-2" data-no-drag>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Checkpoints</span>
            <span>{getCompletedCheckpoints()}/{task.checkpoints.length}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all"
              style={{ width: `${getCheckpointProgress()}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
